import { useReducer, useMemo, useEffect, useContext, useRef, memo } from 'react';
import type { FunctionComponent, ComponentType, Dispatch, Context } from 'react';
import { createPortal } from 'react-dom';

import { createUID, getActiveElement, windowIsAvailable } from '../../utils';
import { useConfiguration, useScrollToggle } from '../../hooks';
import type { ExcludeStrict, OmitStrict } from '../../types';
import '../../init';

import DockedModals from './DockedModals';
import type {
  ModalMethods,
  ModalOptions,
  ModalObject,
  ModalManagerProps,
  ModalState,
  ModalManagerContextValue
} from './Modal.types';
import { ModalContext, ModalManagerContext } from './Contexts';

declare module '../../init' {
  export interface CosmosGlobals {
    modalManagerContext?: Context<ModalManagerContextValue>;
  }
}

type ModalAction =
  | 'create'
  | 'update'
  | 'minimize'
  | 'maximize'
  | 'dock'
  | 'activate'
  | 'dismiss'
  | 'unmount';

type ModalReducerPayload<P extends object> =
  | { action: 'create'; modal: ModalObject<P> }
  | {
      action: 'update';
      modal: Partial<OmitStrict<ModalObject<P>, 'props'>> &
        Pick<ModalObject, 'id'> & { props: Partial<ModalObject<P>['props']> };
    }
  | {
      action: ExcludeStrict<ModalAction, 'create' | 'update'>;
      modal: Pick<ModalObject, 'id'>;
    };

const reducer = <P extends object>(
  modals: ModalObject[],
  payload: ModalReducerPayload<P>
): ModalObject<any>[] => {
  switch (payload.action) {
    case 'create':
      return [...modals, payload.modal];

    case 'unmount':
      return modals.filter(modal => modal.id !== payload.modal.id);

    case 'dismiss':
      return modals.map(modal => {
        modal.state = modal.id === payload.modal.id ? 'closed' : modal.state;
        return modal;
      });

    case 'update':
      return modals.map(modal => {
        modal.props =
          modal.id === payload.modal.id ? { ...modal.props, ...payload.modal.props } : modal.props;
        return modal;
      });

    case 'activate': {
      const newModals = modals.map(modal => {
        modal.state = modal.id === payload.modal.id ? 'open' : modal.state;
        return modal;
      });

      // Move the newly activated modal to the end of the list
      // to ensure it is shown on top of other active modals.
      const activatedModal = newModals.splice(
        newModals.findIndex(modal => modal.id === payload.modal.id),
        1
      );

      return [...newModals, ...activatedModal];
    }

    case 'minimize':
      return modals.map(modal => {
        modal.state =
          modal.id === payload.modal.id && modal.options.minimizable ? 'minimized' : modal.state;
        return modal;
      });

    case 'maximize':
      return modals.map(modal => {
        modal.state =
          modal.id === payload.modal.id && modal.options.maximizable ? 'maximized' : modal.state;
        return modal;
      });

    case 'dock':
      return modals.map(modal => {
        modal.state =
          modal.id === payload.modal.id && modal.options.dockable ? 'docked' : 'minimized';
        return modal;
      });

    default:
      return modals;
  }
};

const WrappedModal = memo(
  ({
    Component,
    id,
    options: { alert, dismissible, dockable, moveable, minimizable, maximizable },
    state,
    methods,
    props,
    top
  }: ModalObject & { top: boolean }) => (
    <ModalContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        id,
        alert,
        dismissible,
        minimizable,
        maximizable,
        dockable,
        moveable,
        state,
        top,
        initialized: true,
        ...methods
      }}
    >
      <Component {...props} />
    </ModalContext.Provider>
  )
);

const RenderModals = ({ modals }: { modals: ModalObject[] }) => {
  const { open, minimized, alerts } = modals.reduce<{
    open: ModalObject[];
    minimized: ModalObject[];
    alerts: ModalObject[];
  }>(
    (obj, modal) => {
      if (modal.options.alert) obj.alerts.push(modal);
      else if (modal.state === 'minimized' || modal.state === 'docked') {
        obj.minimized.push(modal);
      } else obj.open.push(modal);

      return obj;
    },
    { open: [], minimized: [], alerts: [] }
  );

  return (
    <>
      {open.map((modal, i) => (
        <WrappedModal
          {...modal}
          top={alerts.length === 0 && i === open.length - 1}
          key={modal.id}
        />
      ))}
      {minimized.length > 0 && (
        <DockedModals>
          {minimized.map(modal => (
            <WrappedModal {...modal} top={false} key={modal.id} />
          ))}
        </DockedModals>
      )}
      {alerts.map((modal, i) => (
        <WrappedModal {...modal} top={i === open.length - 1} key={modal.id} />
      ))}
    </>
  );
};

const ModalManager: FunctionComponent<ModalManagerProps> = ({ children }) => {
  const [modals, dispatch] = useReducer(reducer, []);
  const { disableScroll, enableScroll } = useScrollToggle();
  const lastFocusedBeforeProgress = useRef<Element | null>(null);

  const providerValue = useMemo(
    () => ({
      create: <P extends object>(
        Component: ComponentType<P>,
        props?: P,
        options?: ModalOptions
      ) => {
        const id = options?.id || createUID();
        const activeElement = lastFocusedBeforeProgress.current ?? getActiveElement();
        lastFocusedBeforeProgress.current = null;
        const initiatorRef = activeElement ? new WeakRef(activeElement) : null;

        const methods: ModalMethods<P> = {
          dismiss: () => {
            dispatch({ action: 'dismiss', modal: { id } });
            options?.onDismiss?.(id);
            const initiator = initiatorRef?.deref();
            const currentActiveElement = getActiveElement();
            if (
              initiator?.isConnected &&
              (initiator instanceof HTMLElement || initiator instanceof SVGElement) &&
              currentActiveElement !== null &&
              (currentActiveElement.closest('[aria-modal="true"]') !== null ||
                currentActiveElement === document.body)
            ) {
              initiator.focus();
            }
          },
          update: updateProps => {
            (dispatch as Dispatch<ModalReducerPayload<P>>)({
              action: 'update',
              modal: { id, props: updateProps }
            });
          },
          minimize: () => {
            dispatch({ action: 'minimize', modal: { id } });
            options?.onMinimize?.(id);
          },
          maximize: () => {
            dispatch({ action: 'maximize', modal: { id } });
            options?.onMaximize?.(id);
          },
          dock: () => {
            dispatch({ action: 'dock', modal: { id } });
            options?.onDock?.(id);
          },
          activate: () => {
            dispatch({ action: 'activate', modal: { id } });
            options?.onActivate?.(id);
          },
          unmount: () => {
            dispatch({ action: 'unmount', modal: { id } });
          }
        };

        let defaultState: ModalState;
        if (options?.defaultMinimized) {
          defaultState = 'minimized';
        } else if (options?.dockable) {
          defaultState = 'docked';
        } else {
          defaultState = 'open';
        }

        (dispatch as Dispatch<ModalReducerPayload<P>>)({
          action: 'create',
          modal: {
            id,
            methods,
            Component,
            props,
            state: defaultState,
            options: {
              alert: false,
              minimizable: false,
              maximizable: false,
              dockable: false,
              moveable: false,
              defaultMinimized: false,
              unmountWhenMinimized: true,
              ...options,
              dismissible: !!(options?.alert ? false : options?.dismissible ?? true)
            }
          }
        });

        return methods;
      },
      ModalContext,
      initialized: true,
      lastFocusedBeforeProgress
    }),
    []
  );

  useEffect(() => {
    if (modals.some(modal => ['open', 'closed', 'maximized'].includes(modal.state))) {
      disableScroll();
    } else {
      enableScroll();
    }
  }, [modals]);

  const { portalTarget } = useConfiguration();

  const context = windowIsAvailable
    ? window.cosmos.modalManagerContext ?? ModalManagerContext
    : ModalManagerContext;
  const priorCtx = useContext(context);

  // Don't create additional Modal portals.
  if (priorCtx.initialized) return <context.Provider value={priorCtx}>{children}</context.Provider>;

  return (
    <context.Provider value={providerValue}>
      {children}
      {modals.length > 0 &&
        portalTarget &&
        createPortal(<RenderModals modals={modals} />, portalTarget)}
    </context.Provider>
  );
};

export default ModalManager;
